import React, { useEffect, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { toast } from "sonner";
import { X, GripVertical, Trash2, Plus, Sparkles, Loader2, CheckCircle2, Save, Eye, History } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BlockEditorFor, BLOCK_DEFAULTS, BLOCK_META } from "./ChallengeBlockEditors";
import { ChallengeBlockList } from "./ChallengeBlockRenderer";
import ChallengeDayVersionHistory from "./ChallengeDayVersionHistory";

function BlockCard({ block, onChangeData, onRemove }) {
  const dragControls = useDragControls();
  const meta = BLOCK_META[block.type] || {};
  const Icon = meta.icon;
  return (
    <Reorder.Item value={block} dragListener={false} dragControls={dragControls} data-testid={`day-block-${block.id}`}
      className="rounded-xl border border-white/10 bg-[#0f1522] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onPointerDown={(e) => dragControls.start(e)} data-testid={`block-drag-handle-${block.id}`} className="cursor-grab text-slate-500 hover:text-white touch-none">
            <GripVertical className="w-4 h-4" />
          </button>
          {Icon && <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />}
          <span className="text-xs font-bold text-white">{meta.label}</span>
        </div>
        <button onClick={() => onRemove(block.id)} data-testid={`block-remove-btn-${block.id}`} className="text-slate-500 hover:text-red-400">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <BlockEditorFor type={block.type} data={block.data} onChange={(data) => onChangeData(block.id, data)} />
    </Reorder.Item>
  );
}

export default function ChallengeDayEditorPanel({ challengeId, day, onClose, onSaved, readOnly = false }) {
  const [title, setTitle] = useState(day.title);
  const [blocks, setBlocks] = useState(day.blocks || []);
  const [instruction, setInstruction] = useState("");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [marking, setMarking] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { setTitle(day.title); setBlocks(day.blocks || []); }, [day.day]);

  const addBlock = (type) => {
    if (type === "quiz" && blocks.some((b) => b.type === "quiz")) { toast.error("Only one quiz block per day is supported."); return; }
    setBlocks((prev) => [...prev, { id: `new-${Date.now()}`, type, data: BLOCK_DEFAULTS[type] }]);
  };
  const changeBlockData = (id, data) => setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, data } : b)));
  const removeBlock = (id) => setBlocks((prev) => prev.filter((b) => b.id !== id));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/custom-challenges/${challengeId}/day/${day.day}`, {
        title, blocks: blocks.map((b) => ({ id: b.id.startsWith("new-") ? undefined : b.id, type: b.type, data: b.data })),
      });
      toast.success(`Day ${day.day} saved`);
      onSaved(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save this day");
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/custom-challenges/${challengeId}/day/${day.day}/regenerate`, { instruction: instruction || null });
      setBlocks(data.blocks || []);
      setTitle(data.title);
      toast.success("Day regenerated with AI — review and save.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to regenerate this day");
    } finally {
      setRegenerating(false);
    }
  };

  const markReviewed = async () => {
    setMarking(true);
    try {
      await api.post(`/custom-challenges/${challengeId}/day/${day.day}/mark-reviewed`);
      toast.success("Marked as reviewed");
      onSaved({ ...day, reviewed: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to mark as reviewed");
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex justify-end" data-testid="challenge-day-editor-panel">
      <div className="w-full sm:w-[560px] h-full bg-[#0a0e17] border-l border-white/10 overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-5">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">Day {day.day}</span>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowHistory(true)} data-testid="day-editor-history-btn" title="Version History" className="text-slate-400 hover:text-white"><History className="w-4 h-4" /></button>
            <button onClick={() => setShowPreview(true)} data-testid="day-editor-preview-btn" title="Preview as Learner" className="text-slate-400 hover:text-white"><Eye className="w-4 h-4" /></button>
            <button onClick={onClose} data-testid="day-editor-close-btn" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <input value={title} onChange={(e) => setTitle(e.target.value)} disabled={readOnly} data-testid="day-editor-title-input"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm font-semibold text-white outline-none focus:border-indigo-400/50 mb-5 disabled:opacity-70" />

        {!readOnly && (
        <div className="rounded-xl border border-indigo-400/20 bg-indigo-500/5 p-4 mb-5">
          <p className="text-xs font-semibold text-indigo-200 mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Regenerate this day with AI</p>
          <input value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Optional: 'make this more hands-on', 'add a quiz instead of reading'..."
            data-testid="day-regenerate-instruction-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-indigo-400/50 mb-2" />
          <button onClick={regenerate} disabled={regenerating} data-testid="day-regenerate-btn"
            className="flex items-center gap-2 text-xs font-semibold bg-indigo-500/20 hover:bg-indigo-500/30 disabled:opacity-60 text-indigo-200 px-3 py-2 rounded-lg">
            {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Regenerate this day with AI
          </button>
          <p className="text-[10px] text-indigo-300/70 mt-2">This replaces the blocks below with a fresh AI draft — review before saving.</p>
        </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Content blocks</p>
          {day.reviewed && <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-300"><CheckCircle2 className="w-3 h-3" /> Reviewed</span>}
        </div>

        <Reorder.Group as="div" axis="y" values={blocks} onReorder={readOnly ? () => {} : setBlocks} className="space-y-3" data-testid="day-blocks-reorder-group">
          {blocks.map((b) => (
            <BlockCard key={b.id} block={b} onChangeData={readOnly ? () => {} : changeBlockData} onRemove={readOnly ? () => {} : removeBlock} />
          ))}
        </Reorder.Group>

        {!readOnly && (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {Object.entries(BLOCK_META).map(([type, meta]) => (
            <button key={type} onClick={() => addBlock(type)} data-testid={`add-block-btn-${type}`}
              className="flex items-center gap-1.5 text-xs font-semibold border border-white/10 hover:border-white/25 text-slate-300 hover:text-white px-3 py-1.5 rounded-lg">
              <Plus className="w-3 h-3" /> {meta.label}
            </button>
          ))}
        </div>
        )}

        {!readOnly && (
        <div className="flex items-center gap-3 mt-6 sticky bottom-0 bg-[#0a0e17] pt-3">
          <button onClick={save} disabled={saving} data-testid="day-editor-save-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Day
          </button>
          {!day.reviewed && (
            <button onClick={markReviewed} disabled={marking} data-testid="day-editor-mark-reviewed-btn"
              className="flex items-center justify-center gap-2 border border-emerald-400/30 hover:bg-emerald-400/10 disabled:opacity-60 text-emerald-300 font-semibold px-4 py-2.5 rounded-xl">
              {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark Reviewed
            </button>
          )}
        </div>
        )}
      </div>

      {showPreview && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center px-4 py-8" data-testid="day-preview-modal">
          <div className="glass-panel rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><Eye className="w-4 h-4 text-indigo-300" /> Preview as Learner — Day {day.day}</h3>
              <button onClick={() => setShowPreview(false)} data-testid="day-preview-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <h4 className="font-heading text-xl font-semibold text-white mb-4">{title}</h4>
            <ChallengeBlockList blocks={blocks} challengeId={challengeId} day={day.day} interactive={false} />
            {blocks.filter((b) => b.type === "quiz").map((b) => (
              <div key={b.id} className="space-y-3" data-testid="preview-quiz-block">
                {(b.data.questions || []).map((q, qi) => (
                  <div key={qi} className="glass-panel rounded-xl p-4">
                    <p className="text-sm font-semibold text-white mb-2">{qi + 1}. {q.question}</p>
                    <div className="space-y-1.5">
                      {(q.options || []).map((opt, oi) => (
                        <div key={oi} className={`text-sm px-3 py-2 rounded-lg border ${oi === q.correct_index ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200" : "border-white/10 text-slate-300"}`}>
                          {opt} {oi === q.correct_index && "✓"}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {showHistory && (
        <ChallengeDayVersionHistory
          challengeId={challengeId} day={day}
          onClose={() => setShowHistory(false)}
          onRestored={(restored) => { setTitle(restored.title); setBlocks(restored.blocks); setShowHistory(false); onSaved(restored); }}
        />
      )}
    </div>
  );
}
